<template>
  <div class="formgrid grid vertical-container">
    <div class="col-1-5 restriction-struct-list" v-show="showFacilitiesMenu">
      <div class="p-inputgroup mb-1">
        <span class="p-inputgroup-addon">
          <i class="pi pi-search"></i>
        </span>
        <InputText placeholder="Поиск..." v-model="searchText" @input="onChangeSearch" />
      </div>
      <StructureFirstItem @onClickItem="onClickItem" :firstItem="firstItem" />
      <ScrollPanel id="scroll_struct" class="scroll-struct">
        <div v-if="isLoadingStruct" class="text-center">
          <ProgressSpinner />
        </div>
        <StructureItems v-else :style="isLoadingForm ? 'pointer-events:none' : ''" :structData="struct"
          @onClickItem="onClickItem" @onClickExpand="onClickExpand" />
      </ScrollPanel>
    </div>
    <div :class="showFacilitiesMenu ? 'col-10-5' : 'col-12'">
      <router-view @loading="changeLoading" />
    </div>
    <div :class="[showFacilitiesMenu ? 'col-10-5' : 'col-12-footer', 'event-footer']">
      <EventData />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import EventData from '../EventData';
import StructureFirstItem from './StructureFirstItem';
import StructureItems from './StructureItems';
import { useMonitoringStructure } from './useMonitoringStructure';

const TYPE_TO_IMG = {
  2: 0,
  3: 10,
  4: 1,
  5: 1,
  15: 9,
  6: 9,
  7: 14,
  14: 14,
  9: 14,
  16: 17,
  17: 22,
  18: 46,
  21: 46,
  19: 40,
};

const store = useStore();
const route = useRoute();
const router = useRouter();

const {
  setStruct,
  clearFacilityData,
} = useMonitoringStructure();

const structService = ref(null);
const isLoadingStruct = ref(true);
const isLoadingForm = ref(false);
const isProcessing = ref(false);
const struct = ref(null);
const structOriginal = ref(null);
const searchText = ref(null);
const firstChange = ref(true);
const scrollTimeOut = ref(null);
const processTimeOut = ref(null);
const firstItem = ref({
  children: [],
  node_type_id: 1,
  facility_id: 0,
  facility_type_id: -1,
  image_index: 8,
  node_name: 'Контроль объектов',
});

const showFacilitiesMenu = computed(() => store.getters.showFacilitiesMenu);

onMounted(() => {
  structService.value = store.getters.nameToService(
    'BaseService',
    store.getters.info.login,
    store.getters.password,
    'structure',
    'data'
  );

  isLoadingStruct.value = true;
  structService.value
    .getData({
      structure_type_id: store.getters.structMode,
      hide_repeat_well: store.getters.hideRepeatWell
    })
    .then((data) => prepareData(data))
    .catch((er) => {
      store.commit('SET_ERROR', {
        er: er?.response ?? er,
        source: ['Structure', 'mounted'],
      });
    })
    .finally(() => {
      isLoadingStruct.value = false;
    });
});

onUnmounted(() => {
  clearTimeout(scrollTimeOut.value);
  clearTimeout(processTimeOut.value);
});

watch(
  () => route.name,
  (newName) => {
    if (newName === 'kron' && structOriginal.value) {
      onFillItem();
    }
  }
);

function onChangeSearch() {
  if (!searchText.value) {
    struct.value = [...structOriginal.value];
    return;
  }
  const searchTerm = searchText.value.toLowerCase();
  struct.value = filterDataWithChildren(structOriginal.value, searchTerm);
}

function filterDataWithChildren(items, searchTerm) {
  return items
    .map((item) => {
      const matches = itemMatches(item, searchTerm);
      const hasMatchingChildren = checkChildren(item.children, searchTerm);

      if (matches || hasMatchingChildren) {
        return {
          ...item,
          children: item.children || [],
        };
      }
      if (item.children && item.children.length > 0) {
        const filteredChildren = filterDataWithChildren(item.children, searchTerm);
        if (filteredChildren.length > 0) {
          return {
            ...item,
            children: filteredChildren,
          };
        }
      }
      return null;
    })
    .filter((item) => item !== null);
}

function itemMatches(item, term) {
  return item.node_name.toLowerCase().includes(term);
}

function checkChildren(children, term) {
  if (!children || children.length === 0) return false;
  return children.some((child) => {
    return itemMatches(child, term) || checkChildren(child.children, term);
  });
}

function changeLoading(load) {
  console.log('changeLoading', load);

  isLoadingForm.value = load;
}

function prepareData(data) {
  const nodeMap = new Map();
  const nodeMapExt = new Map();

  for (const item of data) {
    const uniqueKey = `${item.node_id}_${item.node_name}_${item.node_parent_id || 'root'}`;
    nodeMap.set(uniqueKey, {
      ...item,
      uniqueKey,
      expanded: false,
      image_index: prepareImg(item.node_type_id),
      children: [],
    });
    if (item.node_ext_id) {
      nodeMapExt.set(uniqueKey, {
        ...item,
        uniqueKey,
        expanded: false,
        image_index: prepareImg(item.node_type_id),
        children: [],
      });
    }
  }

  const tree = [];
  const processedNodes = new Set();

  for (const item of data) {
    const uniqueKey = `${item.node_id}_${item.node_name}_${item.node_parent_id || 'root'}`;
    if (processedNodes.has(uniqueKey)) continue;

    const node = nodeMap.get(uniqueKey);
    const parentId = item.node_parent_id;

    if (!parentId || parentId === item.node_id) {
      tree.push(node);
      processedNodes.add(uniqueKey);
    } else {
      let parentFound = false;
      for (const [key, potentialParent] of nodeMap) {
        if (potentialParent.node_id === parentId) {
          if (potentialParent.children) {
            potentialParent.children.push(node);
            parentFound = true;
            processedNodes.add(uniqueKey);
          }
        }
        if (
          potentialParent.node_ext_id === parentId &&
          potentialParent.node_id !== node.node_id
        ) {
          if (potentialParent.children) {
            potentialParent.children.push(node);
            parentFound = true;
          }
        }
      }
      if (!parentFound) {
        tree.push(node);
        processedNodes.add(uniqueKey);
      }
    }
  }

  setStruct([...tree]);
  struct.value = tree;
  structOriginal.value = [...tree];
  onFillItem();
}

function onFillItem() {
  const scrollTo = (id, i) => {
    clearTimeout(scrollTimeOut.value);
    const element = document.getElementById('struct_' + id);
    if (!element) return;
    element.scrollIntoView({ block: 'start', behavior: 'smooth' });
    const ul = document.getElementById('ul_struct');
    const div = ul.closest('div');
    if (div) div.style.top = i > 199 ? '17px' : '15px';
  };

  const { node_type_id, facility_id } = route.params;
  if (node_type_id && facility_id) {
    let i = 0;
    for (const item of struct.value) {
      i++;
      if (item.facility_id == facility_id) {
        if (i > 30) {
          scrollTimeOut.value = setTimeout(() => scrollTo(item.facility_id, i), 300);
        }
        return;
      }
      if (item.children.length > 0) {
        for (const child of item.children) {
          if (child.facility_id == facility_id) {
            item.expanded = true;
            if (i > 30) {
              scrollTimeOut.value = setTimeout(() => scrollTo(child.facility_id, i), 300);
            }
            return;
          }
        }
      }
    }
  } else if (route.name === 'kron') {
    onClickItem({ node: firstItem.value, event: null });
  }
}

function onClickExpand(node) {
  nodeExpand(struct.value, node);
}

function nodeExpand(nodes, node) {
  for (let index = 0; index < nodes.length; index++) {
    if (nodes[index].children?.length) {
      if (nodes[index].node_id === node.node_id) {
        nodes[index].expanded = !nodes[index].expanded;
        return;
      }
      nodeExpand(nodes[index].children, node);
    }
  }
}

async function onClickItem({ node: itemStruct, event }) {
  if (isProcessing.value) return;
  isProcessing.value = true;
  try {
    if (event?.ctrlKey) {
      const url = router.resolve({
        name: 'form',
        params: {
          node_type_id: itemStruct.node_type_id,
          facility_id: itemStruct.facility_id,
          facility_type_id: itemStruct.facility_type_id,
        },
      }).href;
      window.open(url, '_blank');
    } else {
      await clearFacilityData();
      for (const item of struct.value) {
        if (item.children.length > 0) {
          for (const child of item.children) {
            child.active = false;
          }
        }
      }
      router.push({
        name: 'form',
        params: {
          node_type_id: itemStruct.node_type_id,
          facility_id: itemStruct.facility_id,
          facility_type_id: itemStruct.facility_type_id,
        },
      });
    }
  } finally {
    processTimeOut.value = setTimeout(() => (isProcessing.value = false), 300);
  }
}

function prepareImg(type) {
  return TYPE_TO_IMG[type] ? TYPE_TO_IMG[type] : 0;
}

</script>

<style lang="scss" src="./StyleStructure.scss" scoped></style>